import React from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import { SuggestedAction } from '../AskAsperAgentic.types';

export interface FollowupQuestionsProps {
    actions: SuggestedAction[];
    /** When an action has a question, call this to populate the input bar */
    onPopulateInput?: (question: string) => void;
    /** Callback when any followup pill is clicked - used by hosts for analytics. */
    onPillClick?: (action: SuggestedAction) => void;
    'data-test'?: string;
    'data-test-id'?: string;
}

/**
 * FollowupQuestions - Displays followup question pills/buttons
 *
 * This component displays suggested followup actions as pill-shaped buttons.
 * These appear after the assessment is complete, allowing users to take
 * next steps based on the analysis.
 */
const FollowupQuestions: React.FC<FollowupQuestionsProps> = ({
    actions,
    onPopulateInput,
    onPillClick,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();

    if (!actions || actions.length === 0) {
        return null;
    }

    const handleActionClick = (action: SuggestedAction) => {
        onPillClick?.(action);
        if (action.question && onPopulateInput) {
            onPopulateInput(action.question);
        }
        action.onClick?.();
    };

    return (
        <ConfigProvider theme={theme}>
            <div className="space-y-3" data-test={dataTest} data-test-id={dataTestId} data-followup-questions>
                <p className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)]">
                    What would you like to do next?
                </p>
                <div className="flex flex-wrap gap-2">
                    {actions.map((action) => (
                        <button
                            key={action.id}
                            onClick={() => handleActionClick(action)}
                            className="bg-[#F7F5FF] text-[#6B46C1] font-['Open_Sans'] font-normal text-[16px] leading-[20px] px-4 py-2 rounded-[27px] hover:bg-[#E9D5FF] transition-colors text-left"
                            data-test-id={`${dataTestId}-action-${action.id}`}
                        >
                            {action.label}
                        </button>
                    ))}
                </div>
            </div>
        </ConfigProvider>
    );
};

export default FollowupQuestions;
